import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {Grid, Row, Col as UnstyledCol} from 'react-flexbox-grid';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import * as timeline from '../../lib/timeline';
import withUrlState from '../../lib/with-url-state';
import {withUser} from '../../lib/user';
import Screen from '../../components/Screen';
import Button from '../../components/Button';
import Calendar from '../../components/Calendar';
import Content from '../../components/Content';
import SubHeader from '../../components/SubHeader';
import Icon from '../../components/Icon';
import Input from '../../components/Input';
import Placeholder from '../../components/Placeholder';
import SideModal from '../../components/SideModal';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import {T} from '../../components/I18n';

import ReminderCard from './ReminderCard';
import ReminderFormModal from './ReminderFormModal';

class RemindersScreen extends PureComponent {
  state = {
    loading: true,
    error: false,
    reminders: null,
    groups: null
  };

  async componentDidMount() {
    const {credentials} = this.props;

    try {
      const [reminders, groups] = await Promise.all([
        api.getCompanyReminders(credentials),
        api.getUserGroups(credentials)
      ]);

      groups.forEach(group => group.toString = () => group.name);
      reminders.forEach(reminder => {
        reminder.groups.forEach(group => group.toString = () => group.name);
      });
      
      this.setState({reminders, groups});
    }
    catch (e) {
      this.setState({error: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  handleCalendarChange = ([year, month]) => {
    this.props.setUrlState({year, month});
  }

  handleGroupChange = e => {
    this.props.setUrlState({group: e.target.value});
  }

  handleDateDetailsClose = () => {
    this.props.setUrlState({date: null});
  }

  handleDelete = async (reminder, fromCalendarPill = false) => {
    const {credentials} = this.props;

    // eslint-disable-next-line
    if (fromCalendarPill && !confirm('Are you sure you want to delete this reminder?')) {
      return;
    }

    try {
      await api.deleteReminder(reminder, credentials);
      NotificationManager.success("Your reminder was deleted");
      this.setState(prevState => ({
        reminders: prevState.reminders.filter(r => r.id !== reminder.id)
      }));
    }
    catch (e) {
      NotificationManager.error("Unable to delete your reminder. Please try again.");
    }
  }

  handleCloseReminderForm = () => {
    const {history, location: {search}} = this.props;
    history.push(`/reminders${search}`);
  }

  handleSaveReminder = reminder => {
    this.handleCloseReminderForm();
    this.setState(prevState => ({
      reminders: prevState.reminders.filter(r => r.id !== reminder.id).concat([reminder])
    }));
  }

  options(active) {
    const {group} = this.props.urlState;
    const {groups} = this.state;

    return [
      <GroupInput
        type="select" 
        id="group-selection" 
        label="Group:" 
        labelPosition="start"
        value={group}
        onChange={this.handleGroupChange}>
        <option value="">All Groups</option>
        {groups.map(group => (
          <T element="option" noTranslate key={group.id} value={group.id}>{group.name}</T>
        ))}
      </GroupInput>,
      <ViewButtonGroup>
        <Button active={active === 'list'} to={`/reminders?view=list&group=${group}`}>
          <Icon name="list" /> This Week
        </Button>
        <Button active={active === 'calendar'} to={`/reminders?view=calendar&group=${group}`}>
          <Icon name="calendar" /> Calendar
        </Button>
      </ViewButtonGroup>
    ];
  }

  renderListView = () => {
    const {group} = this.props.urlState;
    const {reminders} = this.state;

    const weeklyReminders = timeline.getWeeklyEvents(reminders.filter(filterByGroup(group)).sort(sortReminders));
    const numRemindersThisWeek = weeklyReminders.reduce((count, day) => count + day.events.length, 0);

    if (numRemindersThisWeek < 1) {
      return (
        <Content>
          <SubHeader options={this.options('list')}>This Week</SubHeader>
          <NoReminders>There are no reminders scheduled for this week.</NoReminders>
        </Content>
      );
    }

    return (
      <Content>
        {weeklyReminders.filter(day => day.events.length > 0).map((day, i) => (
          <ReminderSection key={day.date}>
            <SubHeader options={i === 0 && this.options('list')}>
            {timeline.getFriendlyDateName(day.date)}
            </SubHeader>
            <Grid fluid>
              <Row>
                {day.events.map(reminder => (
                  <Col key={reminder.id} md={12} lg={6} xl={4}>
                    <ReminderCard
                      reminder={reminder}
                      onDelete={this.handleDelete}
                    />
                  </Col>
                ))}
              </Row>
            </Grid>
          </ReminderSection>
        ))}
      </Content>
    );
  }

  renderCalendarView = () => {
    const {location: {search}} = this.props;
    const {group, year: yearAsString, month: monthAsString, date: timestamp} = this.props.urlState;
    const {reminders} = this.state;
    const filteredReminders = reminders.filter(filterByGroup(group)).sort(sortReminders);
    const year = parseInt(yearAsString, 10);
    const month = parseInt(monthAsString, 10);
    const selectedDate = timestamp ? new Date(parseInt(timestamp, 10)) : null;

    return (
      <Content>
        <SubHeader options={this.options('calendar')} />
        <CalendarContainer>
          <Calendar year={year} month={month} height="calc(100vh - 20rem)" onChange={this.handleCalendarChange}>
            {date => {
              const remindersForToday = timeline.getEventsForDate(date, filteredReminders);

              return (
                <Calendar.Pills onMore={() => this.props.setUrlState({date: date.getTime()})}>
                  {remindersForToday.map(reminder => (
                    <Calendar.Pill
                      key={reminder.id}
                      to={`/reminders/${reminder.id + search}`}
                      onDelete={() => this.handleDelete(reminder, true)}>
                      {reminder.message}
                    </Calendar.Pill>
                  ))}
                </Calendar.Pills>
              );
            }}
          </Calendar>
        </CalendarContainer>
        <SideModal
          open={selectedDate}
          title={selectedDate && timeline.getFriendlyDateName(selectedDate)}
          onClose={() => this.props.setUrlState({date: null})}>
          {selectedDate && (
            <SideModal.Content>
              {timeline.getEventsForDate(selectedDate, filteredReminders).map(reminder => (
                <CardContainer key={reminder.id}>
                  <ReminderCard
                    reminder={reminder}
                    onDelete={this.handleDelete}
                  />
                </CardContainer>
              ))}
            </SideModal.Content>
          )}
        </SideModal>
      </Content>
    );
  }

  render() {
    const {location: {search}, urlState: {view}, match: {params: {id}}} = this.props;
    const {loading, error, reminders, groups} = this.state;

    return (
      <Screen
        title="Reminders"
        activeLink="Reminders"
        actions={[<Button to={`/reminders/create${search}`}>New Reminder</Button>]}>
        {loading ? (
          <Loading fullScreen />
        ) : error ? (
          <Error fullScreen />
        ) : view === 'list' ? (
          this.renderListView()
        ) : (
          this.renderCalendarView()
        )}
        <ReminderFormModal
          open={id}
          reminder={reminders && reminders.filter(reminder => reminder.id === id)[0]}
          groups={groups}
          onSave={this.handleSaveReminder}
          onClose={this.handleCloseReminderForm}
        />
      </Screen>
    );
  }
}

function filterByGroup(groupId) {
  return reminder => groupId ? reminder.groups.map(group => group.id).indexOf(groupId) >= 0 : true;
}

function sortReminders(a, b) {
  return (b.scheduleTime % 86400000) - (a.scheduleTime % 86400000);
}

const GroupInput = styled(Input)`
  width: 15rem;

  @media (max-width: 50rem) {
    display: none;
  }
`;

const ViewButtonGroup = styled(Button.Group)`
  @media (max-width: 50rem) {
    display: none;
  }
`;

const CalendarContainer = styled.div`
  margin-top: 1rem;
`;

const ReminderSection = styled.div`
  margin-bottom: 2rem;
  
  :last-child {
    margin-bottom: 0;
  }
`;

const Col = styled(UnstyledCol)`
  padding: 1rem;
`;

const NoReminders = styled(Placeholder)`
  height: 8rem;
  margin-top: 2rem;
`;

const CardContainer = styled.div`
  margin-bottom: 2rem;

  :last-child {
    margin-bottom: 0;
  }
`;

const defaultUrlState = {
  view: 'list',
  group: '',
  date: '',
  year: new Date().getFullYear() + '',
  month: new Date().getMonth() + ''
};

export default withUrlState(defaultUrlState)(withUser(RemindersScreen));